import { type KeyboardEvent as ReactKeyboardEvent, type RefObject, useCallback, useEffect } from "react";

import { KEYBOARD_KEYS } from "@/constants/dom";

import getFocusableElements from "./getFocusableElements";

type UseFocusTrapOptions = {
  readonly isActive: boolean;
  readonly containerRef: RefObject<HTMLElement | null>;
};

type TabTargetOptions = {
  readonly container: HTMLElement;
  readonly focusables: readonly HTMLElement[];
  readonly isShiftPressed: boolean;
};

function readActiveElement(): HTMLElement | null {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

function isActiveElement(element: HTMLElement): boolean {
  return element === document.activeElement;
}

function focusInitialElement(container: HTMLElement): void {
  // A child with autoFocus already owns the focus.
  if (container.contains(document.activeElement)) {
    return;
  }

  const [firstFocusable] = getFocusableElements(container);
  (firstFocusable ?? container).focus();
}

// Null means the browser's own Tab order already stays inside the container.
function resolveTabTarget({ container, focusables, isShiftPressed }: TabTargetOptions): HTMLElement | null {
  const firstFocusable = focusables[0];
  const lastFocusable = focusables[focusables.length - 1];
  if (firstFocusable === undefined || lastFocusable === undefined) {
    return container;
  }

  const activeIndex = focusables.findIndex(isActiveElement);
  if (activeIndex === -1) {
    return isShiftPressed ? lastFocusable : firstFocusable;
  }

  if (isShiftPressed && activeIndex === 0) {
    return lastFocusable;
  }

  if (!isShiftPressed && activeIndex === focusables.length - 1) {
    return firstFocusable;
  }

  return null;
}

export default function useFocusTrap({ isActive, containerRef }: UseFocusTrapOptions): (event: ReactKeyboardEvent<HTMLElement>) => void {
  useEffect(() => {
    const container = containerRef.current;
    if (!isActive || container === null) {
      return undefined;
    }

    const previouslyFocused = readActiveElement();
    focusInitialElement(container);

    return () => {
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [isActive, containerRef]);

  return useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const container = containerRef.current;
      if (event.key !== KEYBOARD_KEYS.TAB || container === null) {
        return;
      }

      const nextTarget = resolveTabTarget({
        container,
        focusables: getFocusableElements(container),
        isShiftPressed: event.shiftKey,
      });
      if (nextTarget === null) {
        return;
      }

      event.preventDefault();
      nextTarget.focus();
    },
    [containerRef],
  );
}
